import { OSU_PLAYFIELD_HEIGHT, OSU_PLAYFIELD_WIDTH } from '@shared/circlesChart';
import {
  computeCanvasView,
  fitPlayfield,
} from './circlesPlayfieldGeometry';
import { SceneRenderer, activeKiaiIndex } from './render/circlesDrawScene';
import { HitObjectRenderer } from './render/circlesDrawHitObject';
import { SliderRenderer } from './render/circlesDrawSlider';
import { SpinnerRenderer } from './render/circlesDrawSpinner';
import { CursorRenderer } from './render/circlesDrawCursor';
import { ParticleSystem } from './render/circlesParticles';
import {
  HitBurstSystem,
  ScorePopSystem,
  ShockwaveSystem,
} from './render/circlesHitFx';
import { HitErrorMeter } from './render/circlesDrawHitErrorMeter';
import {
  drawCountdown,
  drawFlashes,
  drawFloatingTexts,
} from './render/circlesDrawOverlays';
import { HudRenderer } from './render/circlesDrawHud';
import { colourForEmission } from './render/circlesRenderColour';
import { HitObjectsPass } from './render/circlesHitObjectsPass';
import type {
  CirclesParticleEmission,
  CirclesRenderInput,
} from './render/circlesRenderTypes';
import { getRenderQuality, type RenderQuality } from '../rendering/renderQuality';

/**
 * Emission types that fire a hit-burst overlay (filled body pop +
 * stroked ring puff) in addition to particles. Misses + taps + death
 * + milestone deliberately do NOT — they have their own visual
 * vocabulary (shake, flash, vignette) and a burst here would
 * over-celebrate failure / under-distinguish background-style cues.
 */
const HIT_BURST_EMISSION_TYPES: ReadonlySet<CirclesParticleEmission['type']> =
  new Set(['hit_perfect', 'hit_good', 'hit_ok']);

/**
 * Top-level renderer for the Circles game mode. Owns the canvas, the
 * frame-pacing clock and one instance of every
 * render subsystem. Per-frame work is delegated — this class is
 * orchestration only, no drawing primitives live here.
 *
 * Subsystems are composed (constructor injection), never inherited.
 * Each owns its own state and exposes a narrow surface
 * (`update`, `draw`, occasional `clear`); the orchestrator picks
 * the per-frame call order so the layered visual stack
 * (background → playfield → hit objects → cursor → particles →
 * shockwaves → hit bursts → flashes → floating text → score pops →
 * hit-error meter → countdown → HUD) reads correctly.
 *
 * Session-facing methods (`emit`, `registerShake`, `recordHitTiming`,
 * the various `clear*` / `testOnly*` hooks) forward to the relevant
 * subsystem without doing any work themselves — keeping the session
 * ↔ renderer contract stable while the internals are free to evolve.
 */
export class CirclesRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private viewWidth = OSU_PLAYFIELD_WIDTH;
  private viewHeight = OSU_PLAYFIELD_HEIGHT;
  private lastFrameAt = performance.now();
  private reducedMotion = false;
  private quality: RenderQuality;

  private scene: SceneRenderer;
  private hitObject: HitObjectRenderer;
  private slider: SliderRenderer;
  private spinner: SpinnerRenderer;
  private cursor: CursorRenderer;
  private particles: ParticleSystem;
  private shockwaves: ShockwaveSystem;
  private hitBursts: HitBurstSystem;
  private scorePops: ScorePopSystem;
  private hitErrorMeter: HitErrorMeter;
  private hud: HudRenderer;
  private hitObjectsPass = new HitObjectsPass();
  private drawnFeedback = new WeakSet<object>();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) {
      throw new Error('Failed to acquire 2d canvas context');
    }
    this.ctx = ctx;
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    this.quality = getRenderQuality();

    this.scene = new SceneRenderer(this.reducedMotion, this.quality);
    this.hitObject = new HitObjectRenderer(this.reducedMotion);
    this.slider = new SliderRenderer(this.reducedMotion, this.quality);
    this.spinner = new SpinnerRenderer(this.reducedMotion, this.quality);
    this.cursor = new CursorRenderer(this.reducedMotion, this.quality);
    this.particles = new ParticleSystem(this.reducedMotion, this.quality);
    this.shockwaves = new ShockwaveSystem(this.quality);
    this.hitBursts = new HitBurstSystem(this.reducedMotion, this.quality);
    this.scorePops = new ScorePopSystem(this.reducedMotion, this.quality);
    this.hitErrorMeter = new HitErrorMeter();
    this.hud = new HudRenderer(this.reducedMotion, this.quality);

    this.resize();
  }

  resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprCap);
    const view = computeCanvasView(this.canvas);
    this.canvas.width = view.viewWidth * dpr;
    this.canvas.height = view.viewHeight * dpr;
    this.dpr = dpr;
    this.viewWidth = view.viewWidth;
    this.viewHeight = view.viewHeight;
  }

  getRenderDpr(): number {
    return this.dpr;
  }

  getViewSize(): { width: number; height: number } {
    return { width: this.viewWidth, height: this.viewHeight };
  }

  /**
   * Map a playfield-space osu coordinate to canvas CSS px. Used by the
   * session to position floating text / particle emissions.
   */
  playfieldToCss(osuX: number, osuY: number): { x: number; y: number } {
    const rect = fitPlayfield(this.viewWidth, this.viewHeight);
    return { x: rect.x + osuX * rect.scale, y: rect.y + osuY * rect.scale };
  }

  /**
   * Drop the per-spinner draw cache. Called by the session on loop
   * wrap so the renderer's smoothed-intensity carry doesn't pull a
   * stale value from the previous loop into the new run's first
   * spinner draw.
   */
  clearSpinDrawCache(): void {
    this.spinner.clearCache();
  }

  /**
   * Drop FX keyed by hit-object id (shakes) and kiai-burst tracking.
   * Called on loop boundaries so a residual shake mid-flight when
   * loop N wraps doesn't re-fire on loop N+1's fresh play of the
   * identically-keyed circle. Hit-error history intentionally
   * survives the wrap — the player's timing trend is still
   * informative across loops.
   *
   * Also clears the kiai-burst tracker so the section-start star
   * burst fires once per kiai section per LOOP, not once per session.
   * Without this the dramatic "music shifting gear" moment would only
   * register on loop 1 and silently no-op on every subsequent
   * playthrough of the same chart.
   *
   * Resets HP tracking too so the next frame snaps `displayedHp` to
   * the engine's freshly-reset value (typically maxHp at loop start)
   * instead of treating the jump from end-of-loop HP to full HP as a
   * damage event.
   */
  clearLoopBoundFx(): void {
    this.hitObject.clearShakes();
    this.scene.clearLoopBoundFx();
    this.hud.clearHpTracking();
    this.hitObjectsPass.reset();
  }

  /**
   * Convert a length expressed in osu units into canvas CSS px,
   * matching the playfield's current letterbox-aware scale. Used by
   * the session to size shockwave rings around hit objects so the
   * waves track the same canvas-relative size as the circles
   * themselves.
   */
  osuLengthToCss(osuLength: number): number {
    return osuLength * fitPlayfield(this.viewWidth, this.viewHeight).scale;
  }

  /**
   * CSS-px anchor for HP-loss damage floaters. Lives just below the
   * top-centre HP bar so the "-N HP" label rises *toward* the bar —
   * the same place the bleed-segment + bar shake fire. Co-locating
   * both pieces of feedback in one spot makes damage read as a
   * single coherent event instead of two disconnected pulses.
   *
   * Numbers stay in sync with {@link HpBarRenderer.draw}'s layout
   * (barY=22, barH=16, numeric-text baseline at y + barH + 14 = 52)
   * plus a 28-px gap so the floater's punch-in scale-up doesn't
   * collide with the readout.
   */
  getHudHpDamageAnchor(): { x: number; y: number } {
    return { x: this.viewWidth / 2, y: 80 };
  }

  /**
   * CSS-px anchor for score-pop "+N" flyers. Points at the visual
   * centre of the live score readout in {@link HudRenderer.draw} (52-px bold
   * digits anchored left at `HUD_LEFT_X = 24`, baseline y=78). A
   * single digit lands the pop right on the readout; multi-digit
   * scores extend rightward of this anchor, which is acceptable since
   * the pop arc decelerates into a fade rather than physically docking
   * onto a glyph.
   */
  getHudScoreAnchor(): { x: number; y: number } {
    return { x: 60, y: 60 };
  }

  /** Render-harness hook — forwards to the HUD's HP bar. See {@link HpBarRenderer.testOnlyPrimeHpForHarness}. */
  testOnlyPrimeHpForHarness(priorHp: number): void {
    this.hud.testOnlyPrimeHpForHarness(priorHp);
  }

  /**
   * Public emission entry point. Sessions don't hold particle pools —
   * they push emissions here and the particle subsystem owns the
   * pool, lifetime, and shape mix.
   *
   * Successful-hit emissions also trigger a hit-burst overlay (body
   * pop + ring puff) at the same coordinates so the visual feedback
   * for a clean hit comes through as one cohesive event — burst on
   * top, particles below — rather than the session having to push
   * the burst as a separate sibling stream.
   */
  emit(emission: CirclesParticleEmission): void {
    this.particles.emit(emission);
    if (HIT_BURST_EMISSION_TYPES.has(emission.type)) {
      this.hitBursts.spawn(
        emission.x,
        emission.y,
        emission.colour ?? colourForEmission(emission.type),
      );
    }
  }

  /**
   * Start (or refresh) a horizontal-wobble shake on the named circle.
   * Called by the session in response to engine `circle_shake` events
   * — the player clicked on an approaching but not-yet-hittable
   * circle. Forwarded to the hit-object subsystem which owns the
   * per-circle shake timers.
   */
  registerShake(hitObjectId: string): void {
    this.hitObject.registerShake(hitObjectId);
  }

  /**
   * Log a successful hit's timing for the hit-error meter. Misses
   * are excluded — their `diffMs` reflects auto-resolution beat
   * timing, not actual player input.
   */
  recordHitTiming(diffMs: number, verdict: 'perfect' | 'good' | 'ok'): void {
    this.hitErrorMeter.record(diffMs, verdict);
  }

  draw(input: CirclesRenderInput): void {
    // The session feeds us its `ResizeObserver`-cached rect. A 0×0
    // input means the canvas hasn't laid out yet (initial frame
    // before the first RO callback fires); skipping the frame is
    // safer than calling `resize()` → `computeCanvasView()` →
    // `getBoundingClientRect()` and forcing a layout flush on every
    // pre-layout frame.
    if (input.width <= 0 || input.height <= 0) {
      return;
    }
    if (
      Math.floor(input.width) !== this.viewWidth ||
      Math.floor(input.height) !== this.viewHeight
    ) {
      this.resize();
    }

    const now = performance.now();
    // Feedback expires in wall time even when frames are slow; only motion
    // integration (notably the cursor spring) needs a bounded step.
    const elapsedDt = (now - this.lastFrameAt) / 1000;
    const animDt = Math.min(0.05, elapsedDt);
    this.lastFrameAt = now;
    const catchUpDt = elapsedDt - animDt;
    // Pools already owned by the renderer predate this frame's incoming events.
    if (catchUpDt > 0) {
      this.particles.update(catchUpDt);
      this.shockwaves.update(catchUpDt);
      this.scorePops.update(catchUpDt);
    }
    // Session queues contain both old and freshly-created feedback. New verdicts
    // must remain visible on their first frame, including after a long stall.
    for (const flash of input.flashes) {
      if (this.drawnFeedback.has(flash)) {
        flash.t -= catchUpDt;
      }
      this.drawnFeedback.add(flash);
    }
    for (const text of input.floatingTexts) {
      if (this.drawnFeedback.has(text)) {
        text.life -= catchUpDt;
      }
      this.drawnFeedback.add(text);
    }

    // Drain session-side queues into subsystem-owned pools. The queues
    // are mutated in place (length = 0) so the session doesn't have to
    // hold a renderer reference to clear them.
    for (const emission of input.particleEmissions) {
      this.particles.emit(emission);
    }
    input.particleEmissions.length = 0;

    if (input.shockwaves.length > 0) {
      this.shockwaves.ingest(input.shockwaves);
      input.shockwaves.length = 0;
    }
    if (input.scorePops.length > 0) {
      this.scorePops.ingest(input.scorePops);
      input.scorePops.length = 0;
    }

    const ctx = this.ctx;
    const w = this.viewWidth;
    const h = this.viewHeight;
    const shakeAmount = this.reducedMotion ? 0 : input.shake.amount;
    const shakeX = shakeAmount > 0.01 ? (Math.random() - 0.5) * shakeAmount * 2 : 0;
    const shakeY = shakeAmount > 0.01 ? (Math.random() - 0.5) * shakeAmount * 2 : 0;

    ctx.save();
    ctx.scale(this.dpr, this.dpr);
    ctx.translate(shakeX, shakeY);

    const kiaiIdx = activeKiaiIndex(input.beat, input.chart.kiaiSections);
    this.scene.drawBackground(
      ctx,
      input.chart,
      input.state,
      input.beat,
      w,
      h,
      input.beatPulse,
      kiaiIdx,
    );
    this.scene.drawStars(ctx, w, h, animDt, input.paused ? 0 : 1);

    const field = fitPlayfield(w, h);
    this.scene.drawPlayfieldFrame(ctx, field, input.chart, input.beatPulse);
    // Spawn the per-section star burst the moment we cross a kiai
    // boundary (paused frames, scrubbing, or any non-monotonic time
    // jump still only fire once per index per session).
    this.scene.maybeSpawnKiaiBurst(kiaiIdx, field, input.chart, this.particles.particles);

    this.hitObjectsPass.draw(ctx, input.chart, input.state, input.beat, field, kiaiIdx, {
      hitObject: this.hitObject,
      slider: this.slider,
      spinner: this.spinner,
      dpr: this.dpr,
      reducedMotion: this.reducedMotion,
      quality: this.quality,
    });

    this.cursor.updateTrail(input.cursor, animDt);
    this.cursor.drawTrail(ctx, field);
    this.cursor.updateScale(input.cursorPressed, animDt);
    this.cursor.draw(ctx, input.cursor, field);

    this.particles.update(animDt);
    this.particles.draw(ctx);

    this.shockwaves.update(animDt);
    this.shockwaves.draw(ctx);

    // Hit bursts drawn after particles + shockwaves so the body
    // pop + ring puff sit on top of the burst haze rather than
    // being washed out by it.
    this.hitBursts.update(animDt);
    this.hitBursts.draw(ctx, input.chart, field);

    // Shake timers are display-only; the engine never sees these.
    this.hitObject.updateShakes(animDt);

    drawFlashes(ctx, input.flashes, w, h, animDt);
    drawFloatingTexts(ctx, input.floatingTexts, animDt, this.reducedMotion, this.quality);
    this.scorePops.update(animDt);
    this.scorePops.draw(ctx);
    this.hitErrorMeter.update(animDt);
    this.hitErrorMeter.draw(ctx, input.chart, w, h);
    drawCountdown(ctx, input.countdown, w, h, this.quality);
    this.hud.draw(
      ctx,
      input.state,
      input.beat,
      input.chart,
      w,
      animDt,
      input.displayedScore ?? input.state.score,
    );

    ctx.restore();
  }
}
